import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { usePayment } from '../../hooks/usePayment';
import { useFeeReceipt } from '../../hooks/useFeeReceipt';
import { useCurrentStudent } from '../../hooks/useCurrentStudent';
import { useError } from '../../contexts/ErrorContext';
import { useFeeStructureBySessionAndClass } from '../../hooks/useFeeStructure';
import { useSchools } from '../../hooks/useSchools';
import { useAuth } from '../../contexts/AuthContext';
import { Order, PaymentVerificationDto } from '../../services/paymentService';
import EnhancedTable from '../../components/common/EnhancedTable';
import PaymentIcon from '@mui/icons-material/Payment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { format } from 'date-fns';

type PaymentType = 'INSTALLMENT' | 'ONE_TIME';

const FeePaymentManagement: React.FC = () => {
  const { t } = useTranslation();
  const { createOrderMutation } = usePayment();
  const { verifyPaymentMutation } = useFeeReceipt();
  const { studentData, studentLoading } = useCurrentStudent();
  const { showError } = useError();
  const { user } = useAuth();
  const { useSchoolById, useSchoolLogoInfo } = useSchools();

  const [payFeesModalOpen, setPayFeesModalOpen] = useState(false);
  const [viewFeeStructureModalOpen, setViewFeeStructureModalOpen] = useState(false);
  const [paymentType, setPaymentType] = useState<PaymentType>('INSTALLMENT');
  const [amount, setAmount] = useState<string>('');
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [schoolLogoUrl, setSchoolLogoUrl] = useState<string | null>(null);
  const razorpayLoaded = useRef(false);

  // Get schoolId from user
  const schoolId = useMemo(() => {
    return user?.schoolId || studentData?.schoolId;
  }, [user?.schoolId, studentData?.schoolId]);

  // Fetch school data for logo and name
  const { data: schoolData } = useSchoolById(schoolId);
  const { data: logoInfo } = useSchoolLogoInfo(schoolId);

  // Load school logo
  useEffect(() => {
    const fetchLogo = async () => {
      try {
        if (logoInfo && logoInfo.fileName) {
          const url = `${process.env.REACT_APP_API_URL}/schools/logos/${logoInfo.fileName}`;
          setSchoolLogoUrl(url);
          if (schoolId) {
            localStorage.setItem(`school_logo_${schoolId}`, url);
          }
          return;
        }

        if (schoolId) {
          const cached = localStorage.getItem(`school_logo_${schoolId}`);
          if (cached) {
            setSchoolLogoUrl(cached);
            return;
          }
        }

        setSchoolLogoUrl(null);
      } catch (err) {
        setSchoolLogoUrl(null);
      }
    };

    if (schoolId && logoInfo !== undefined) {
      fetchLogo();
    }
  }, [schoolId, logoInfo]);

  // Load Razorpay script on component mount
  useEffect(() => {
    // Check if Razorpay is already loaded
    if ((window as any).Razorpay) {
      razorpayLoaded.current = true;
      return;
    }

    // Check if script is already in the DOM
    const existingScript = document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]');
    if (existingScript) {
      // Script exists, wait for it to load
      const checkRazorpay = setInterval(() => {
        if ((window as any).Razorpay) {
          razorpayLoaded.current = true;
          clearInterval(checkRazorpay);
        }
      }, 100);

      setTimeout(() => clearInterval(checkRazorpay), 5000);
      return;
    }

    // Create and load script
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => {
      // Double check Razorpay is available
      if ((window as any).Razorpay) {
        razorpayLoaded.current = true;
      } else {
      }
    };
    script.onerror = () => {
      razorpayLoaded.current = false;
    };
    document.body.appendChild(script);
  }, []);

  // Fetch fee structure only when modal is open (when Pay Fees button is clicked)
  const { data: feeStructure, isLoading: feeStructureLoading } = useFeeStructureBySessionAndClass(
    studentData?.sessionId,
    studentData?.className,
    payFeesModalOpen && !!studentData?.sessionId && !!studentData?.schoolClassId
  );

  // Fetch fee structure for view modal
  const { data: viewFeeStructure, isLoading: viewFeeStructureLoading } = useFeeStructureBySessionAndClass(
    studentData?.sessionId,
    studentData?.className,
    viewFeeStructureModalOpen && !!studentData?.sessionId && !!studentData?.schoolClassId
  );

  // Update amount when payment type or fee structure changes
  useEffect(() => {
    if (!feeStructure || !payFeesModalOpen) return;

    if (paymentType === 'ONE_TIME') {
      // Set total annual fees for one-time payment
      const totalAmount = feeStructure.totalAnnualFees || 0;
      setAmount(totalAmount.toString());
    } else if (paymentType === 'INSTALLMENT') {
      // Set first installment amount
      const firstInstallment = feeStructure.feeInstallments?.[0];
      const installmentAmount = firstInstallment?.amount || 0;
      setAmount(installmentAmount.toString());
    }
  }, [paymentType, feeStructure, payFeesModalOpen]);


  const handleOpenPayFeesModal = () => {
    setPayFeesModalOpen(true);
    // Reset payment type to default
    setPaymentType('INSTALLMENT');
    setAmount(''); // Reset amount - will be populated when fee structure loads
  };

  const handleClosePayFeesModal = () => {
    setPayFeesModalOpen(false);
    setPaymentType('INSTALLMENT');
    setAmount('');
  };

  const handlePaymentTypeChange = (event: any) => {
    const newPaymentType = event.target.value as PaymentType;
    setPaymentType(newPaymentType);

    // Update amount immediately when payment type changes
    if (feeStructure) {
      if (newPaymentType === 'ONE_TIME') {
        // Set total annual fees for one-time payment
        const totalAmount = feeStructure.totalAnnualFees || 0;
        setAmount(totalAmount.toString());
      } else if (newPaymentType === 'INSTALLMENT') {
        // Set first installment amount
        const firstInstallment = feeStructure.feeInstallments?.[0];
        const installmentAmount = firstInstallment?.amount || 0;
        setAmount(installmentAmount.toString());
      }
    }
  };

  const handlePayFees = async () => {
    const feeAmount = parseFloat(amount);

    if (!feeAmount || feeAmount <= 0) {
      showError(t('please_enter_valid_amount'));
      return;
    }

    if (!studentData) {
      showError(t('student_information_not_available'));
      return;
    }

    // Validate required student data
    if (!studentData.studentCode || !studentData.studentName || !studentData.schoolClassId || !studentData.sessionId) {
      showError(t('student_information_incomplete_check_profile'));
      return;
    }

    try {
      // Prepare order request with all required fields
      const orderRequest = {
        amount: feeAmount,
        studentCode: studentData.studentCode,
        studentName: studentData.studentName,
        classId: studentData.schoolClassId,
        sessionId: studentData.sessionId,
      };

      // Call payFee function (which internally calls createOrder)
      const createOrderResponse = await createOrderMutation.mutateAsync(orderRequest);

      // Validate order response structure
      if (!createOrderResponse || !createOrderResponse.orderId) {
        showError(t('failed_create_payment_order'));
        return;
      }

      // Validate order ID format
      if (!createOrderResponse.orderId.startsWith('order_')) {
        showError(t('invalid_payment_order_format'));
        return;
      }

      // Check if Razorpay key is configured before initializing
      const razorpayKey = process.env.REACT_APP_RAZORPAY_KEY_ID;

      if (!razorpayKey || razorpayKey === 'YOUR_KEY_ID' || razorpayKey.trim() === '') {
        showError(t('payment_gateway_not_configured'));
        return;
      }

      // Validate key format
      if (!razorpayKey.startsWith('rzp_test_') && !razorpayKey.startsWith('rzp_live_')) {
        showError(t('invalid_payment_gateway_configuration'));
        return;
      }

      // IMPORTANT: Warn about key mismatch

      const orderForRazorpay: Order = {
        id: createOrderResponse.id ? parseInt(createOrderResponse.id) : undefined,
        orderId: createOrderResponse.orderId,
        amount: createOrderResponse.amount,
        currency: createOrderResponse.currency,
        receipt: createOrderResponse.receipt,
        status: createOrderResponse.status,
        studentName: studentData.studentName,
        studentCode: studentData.studentCode,
        classId: studentData.schoolClassId,
        sessionId: studentData.sessionId,
      };

      initializeRazorpay(orderForRazorpay, feeAmount);
    } catch (error: any) {
      setPaymentError(error?.response?.data?.message || t('failed_process_payment'));
      setPaymentDialogOpen(true);
    }
  };

  // Initialize Razorpay payment
  const initializeRazorpay = (orderResponse: Order, amount: number) => {
    // Check if Razorpay is already available
    if ((window as any).Razorpay) {
      razorpayLoaded.current = true;
      openRazorpayCheckout(orderResponse, amount);
      return;
    }

    // If script is not loaded, try to load it first
    const existingScript = document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]');
    if (!existingScript) {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => {
        if ((window as any).Razorpay) {
          razorpayLoaded.current = true;
          openRazorpayCheckout(orderResponse, amount);
        } else {
          showError(t('payment_gateway_failed_initialize'));
        }
      };
      script.onerror = () => {
        showError(t('payment_gateway_failed_load'));
      };
      document.body.appendChild(script);
      return;
    }

    // Wait for Razorpay script to load
    let attempts = 0;
    const maxAttempts = 50; // 5 seconds (50 * 100ms)

    const checkRazorpay = setInterval(() => {
      attempts++;

      if ((window as any).Razorpay) {
        razorpayLoaded.current = true;
        clearInterval(checkRazorpay);
        openRazorpayCheckout(orderResponse, amount);
      } else if (attempts >= maxAttempts) {
        clearInterval(checkRazorpay);
        showError(t('payment_gateway_failed_load'));
      }
    }, 100);
  };

  // Open Razorpay checkout
  // NOTE: When rzp1.open() is called, Razorpay automatically calls the preferences API internally
  // This is part of Razorpay's standard checkout flow and cannot be disabled
  // If you get a 400 Bad Request, it's usually due to:
  // 1. Key mismatch between backend (order creation) and frontend (checkout)
  // 2. Invalid order_id format or order doesn't exist
  // 3. Order was created with different credentials
  const openRazorpayCheckout = (orderResponse: Order, amount: number) => {
    const Razorpay = (window as any).Razorpay;

    if (!Razorpay) {
      showError(t('payment_gateway_not_available'));
      return;
    }

    // Get Razorpay key from environment variable
    const razorpayKey = process.env.REACT_APP_RAZORPAY_KEY_ID;

    // Validate Razorpay key is configured
    if (!razorpayKey || razorpayKey === 'YOUR_KEY_ID' || razorpayKey.trim() === '') {
      showError(t('payment_gateway_not_configured'));
      return;
    }

    // Validate Razorpay key format (should start with rzp_test_ or rzp_live_)
    if (!razorpayKey.startsWith('rzp_test_') && !razorpayKey.startsWith('rzp_live_')) {
      showError(t('invalid_payment_gateway_configuration'));
      return;
    }

    // Validate order response structure
    if (!orderResponse) {
      showError(t('invalid_order_try_again'));
      return;
    }

    // Validate that orderId exists and has correct format
    if (!orderResponse.orderId) {
      showError(t('invalid_order_id_missing'));
      return;
    }

    // Validate orderId format (Razorpay order IDs must start with "order_")
    if (!orderResponse.orderId.startsWith('order_')) {
      showError(t('invalid_payment_order_format'));
      return;
    }

    // Validate order amount
    const orderAmount = orderResponse.amount || amount;
    if (!orderAmount || orderAmount <= 0 || isNaN(orderAmount)) {
      showError(t('invalid_payment_amount'));
      return;
    }

    // Validate order currency
    if (!orderResponse.currency || orderResponse.currency.trim() === '') {
      showError(t('invalid_order_currency_missing'));
      return;
    }

    // Validate order status (should be "created" for new orders)
    if (orderResponse.status && orderResponse.status !== 'created') {
    }

    // Validate amount
    if (!orderAmount || orderAmount <= 0) {
      showError(t('invalid_payment_amount'));
      return;
    }

    // Clean and validate description
    // Remove any characters that might cause issues with Razorpay API
    // Keep only alphanumeric, spaces, and basic punctuation
    const studentName = (orderResponse.studentName || studentData?.studentName || 'Student').trim();
    const description = `Fee Payment - ${studentName}`
      .replace(/[^\w\s\-.,!?]/g, '') // Remove special characters except basic punctuation
      .substring(0, 255)
      .trim();

    // Validate image URL if provided - must be a valid HTTPS URL
    let imageUrl: string | undefined = undefined;
    if (schoolLogoUrl) {
      try {
        const url = new URL(schoolLogoUrl);
        // Razorpay requires HTTPS for image URLs
        if (url.protocol === 'https:') {
          imageUrl = schoolLogoUrl;
        } else {
        }
      } catch (e) {
      }
    }

    // Clean prefill data - ensure all values are valid strings
    // Razorpay expects: name (string), email (valid email), contact (digits only, 10-15 chars)
    const prefillData: { name?: string; email?: string; contact?: string } = {};

    if (studentData?.studentName) {
      const name = studentData.studentName.trim().substring(0, 100);
      if (name.length > 0) {
        prefillData.name = name;
      }
    }

    if (studentData?.gmail || user?.email) {
      const email = (studentData?.gmail || user?.email || '').trim();
      // Basic email validation
      if (email && email.includes('@') && email.length <= 100) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (emailRegex.test(email)) {
          prefillData.email = email;
        }
      }
    }

    if (studentData?.mobileNo) {
      const contact = studentData.mobileNo.replace(/\D/g, ''); // Remove non-digits
      // Razorpay expects 10-15 digits for contact
      if (contact.length >= 10 && contact.length <= 15) {
        prefillData.contact = contact;
      }
    }

    // Clean notes - ensure all values are strings (Razorpay requires string values only)
    // Notes are optional metadata, keep them simple
    const notesData: Record<string, string> = {};

    if (orderResponse.studentCode || studentData?.studentCode) {
      const code = String(orderResponse.studentCode || studentData?.studentCode || '').trim();
      if (code.length > 0 && code.length <= 100) {
        notesData.studentCode = code;
      }
    }

    if (orderResponse.studentName || studentData?.studentName) {
      const name = String(orderResponse.studentName || studentData?.studentName || '').trim();
      if (name.length > 0 && name.length <= 100) {
        notesData.studentName = name;
      }
    }

    // Convert numbers to strings for notes
    if (orderResponse.classId || studentData?.schoolClassId) {
      notesData.classId = String(orderResponse.classId || studentData?.schoolClassId || '');
    }

    if (orderResponse.sessionId || studentData?.sessionId) {
      notesData.sessionId = String(orderResponse.sessionId || studentData?.sessionId || '');
    }

    // Build Razorpay options object
    // IMPORTANT: When using order_id, DO NOT pass amount or currency
    // Razorpay will get these from the order automatically
    // Only include valid, properly formatted values to avoid 400 Bad Request errors
    const options: any = {
      key: razorpayKey, // Required: Razorpay Key ID
      name: (schoolData?.nameofSchool || 'School Management').trim().substring(0, 100), // Business name (max 100 chars)
      description: description, // Payment description (max 255 chars, sanitized)
      order_id: orderResponse.orderId, // Required: Order ID from createOrder response
      handler: function (response: any) {
        // Payment success handler
        handlePaymentSuccess(response, orderResponse);
      },
    };

    // Only add optional fields if they have valid values
    // Invalid or undefined values can cause Razorpay to return 400 Bad Request

    if (imageUrl) {
      options.image = imageUrl; // Valid HTTPS URL only
    }

    // Only add prefill if we have at least one valid field
    if (Object.keys(prefillData).length > 0) {
      options.prefill = prefillData;
    }

    // Only add notes if we have data (all values must be strings)
    if (Object.keys(notesData).length > 0) {
      options.notes = notesData;
    }

    // Theme configuration (optional)
    options.theme = {
      color: '#3092e3', // Brand color
    };

    // Modal configuration (optional)
    options.modal = {
      ondismiss: function () {
        showError(t('payment_cancelled'));
      },
    };

    // Final validation before creating Razorpay instance
    if (!options.key || !options.order_id) {    
      showError(t('payment_configuration_error'));
      return;
    }

    // Validate order_id format one more time
    if (!options.order_id.startsWith('order_')) {
      showError(t('invalid_payment_order'));
      return;
    }

    // Remove any undefined or null values from options to prevent 400 errors
    // Razorpay doesn't accept undefined/null values in the options object
    const cleanOptions: any = {};
    Object.keys(options).forEach((key) => {
      const value = options[key];
      // Skip undefined, null, empty strings, and empty objects
      if (value !== undefined && value !== null && value !== '') {
        if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) {
          // Skip empty objects
          return;
        }
        cleanOptions[key] = value;
      }
    });

    try {
      // Create Razorpay instance with cleaned options
      // NOTE: When rzp1.open() is called, Razorpay will automatically:
      // 1. Call the preferences API internally to validate the order
      // 2. Open the checkout UI if validation succeeds
      // 
      // If you get 400 Bad Request "Invalid request payload", check:
      // - Backend and frontend use the SAME Razorpay key
      // - Order ID is valid and exists in Razorpay
      // - Order was created with the same key as frontend
      // - All option values are properly formatted (no undefined/null)
      // - Description doesn't contain invalid characters
      // - Image URL is valid HTTPS URL
      // - Prefill email/contact are properly formatted
      // - Notes values are all strings
      const rzp1 = new Razorpay(cleanOptions);

      rzp1.on('payment.failed', function (response: any) {
        // Payment failed handler
        handlePaymentFailure(response);
      });

      // Handle Razorpay errors (including 400 Bad Request from preferences API)
      rzp1.on('error', function (error: any) {
        // Check for common 400 Bad Request causes
        if (error.error?.code === 'BAD_REQUEST_ERROR' || error.error?.step === 'preferences') {
        }
        // Show more specific error message
        let errorMessage = 'Payment gateway error. Please try again.';
        if (error.error?.description) {
          errorMessage = error.error.description;
        } else if (error.error?.reason) {
          errorMessage = `Payment error: ${error.error.reason}`;
        } else if (error.error?.code === 'BAD_REQUEST_ERROR' || error.error?.step === 'preferences') {
          errorMessage = t('payment_configuration_error');
        }
        showError(errorMessage);
      });

      // Open Razorpay checkout
      rzp1.open();
    } catch (error: any) {
      showError(t('failed_initialize_payment_gateway'));
    }
  };

  // Handle payment success
  // According to Razorpay docs: After payment success, verify the payment signature on server
  // Response contains: razorpay_payment_id, razorpay_order_id, razorpay_signature
  const handlePaymentSuccess = async (razorpayResponse: any, orderResponse: Order) => {
    try {
      // Close the pay fees modal
      handleClosePayFeesModal();

      // Validate required data
      if (!razorpayResponse.razorpay_payment_id || !razorpayResponse.razorpay_order_id || !razorpayResponse.razorpay_signature) {
        showError(t('payment_response_invalid'));
        return;
      }

      if (!studentData || !studentData.studentCode || !studentData.studentName || !studentData.schoolClassId || !studentData.sessionId) {
        showError(t('student_information_incomplete_contact_support'));
        return;
      }

      if (!orderResponse.amount) {
        showError(t('order_information_incomplete'));
        return;
      }

      // Prepare payment verification data as per Razorpay documentation
      // Step 1.5: Verify Payment Signature
      const paymentVerificationData: PaymentVerificationDto = {
        paymentId: razorpayResponse.razorpay_payment_id,
        orderId: razorpayResponse.razorpay_order_id,
        signature: razorpayResponse.razorpay_signature,
        amount: orderResponse.amount,
        studentCode: studentData.studentCode,
        studentName: studentData.studentName,
        classId: studentData.schoolClassId,
        sessionId: studentData.sessionId,
      };

      // Call verification API to verify payment signature on server
      const feeReceipt = await verifyPaymentMutation.mutateAsync(paymentVerificationData);


      // Show success message
      setPaymentSuccess(true);
      setPaymentDialogOpen(true);
      setAmount(''); // Reset amount

      // Optionally, you can refresh fee receipt data here
      // queryClient.invalidateQueries({ queryKey: ['feeReceipts'] });
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || t('payment_successful_verification_failed');
      showError(errorMessage);
      setPaymentError(errorMessage);
      setPaymentDialogOpen(true);
    }
  };

  // Handle payment failure
  const handlePaymentFailure = (response: any) => {
    setPaymentError(response.error?.description || t('payment_failed_try_again'));
    setPaymentDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setPaymentDialogOpen(false);
    setPaymentSuccess(false);
    setPaymentError(null);
  };

  const handleOpenViewFeeStructureModal = () => {
    setViewFeeStructureModalOpen(true);
  };

  const handleCloseViewFeeStructureModal = () => {
    setViewFeeStructureModalOpen(false);
  };

  if (studentLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!studentData) {
    return (
      <Box p={3}>
        <Alert severity="error">{t('student_information_not_available')}</Alert>
      </Box>
    );
  }

  // Check if fee payment service is enabled
  if (schoolData && !schoolData.feePaymentService) {
    return (
      <Box p={3}>
        <Alert severity="warning">
          {t('fee_payment_service_disabled') || "Fee payment service is not enabled for your school."}
        </Alert>
      </Box>
    );
  }

  // Mock data for fee payment history - replace with actual API call
  const feePaymentHistory: any[] = [];

  const columns = [
    {
      id: 'orderId',
      label: t('order_id'),
      minWidth: 150,
      align: 'left' as const,
    },
    {
      id: 'amount',
      label: t('amount_rupee'),
      minWidth: 120,
      align: 'right' as const,
      format: (value: number) => `₹${value.toFixed(2)}`,
    },
    {
      id: 'localDateTime',
      label: t('payment_date'),
      minWidth: 150,
      align: 'left' as const,
      format: (value: string) => {
        if (!value) return '-';
        const date = new Date(value);
        return date.toLocaleDateString('en-IN', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      },
    },
    {
      id: 'status',
      label: t('status'),
      minWidth: 100,
      align: 'center' as const,
      format: (value: string) => {
        const statusColors: { [key: string]: string } = {
          SUCCESS: '#4caf50',
          PENDING: '#ff9800',
          FAILED: '#f44336',
        };
        return (
          <Typography
            sx={{
              color: statusColors[value] || '#666',
              fontWeight: 'bold',
              textTransform: 'capitalize',
            }}
          >
            {value || t('not_available_short')}
          </Typography>
        );
      },
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#333' }}>
          <PaymentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          {t('fee_payments') || 'Fee & Payments'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<VisibilityIcon />}
            onClick={handleOpenViewFeeStructureModal}
            sx={{
              borderColor: '#3092e3',
              color: '#3092e3',
              '&:hover': {
                borderColor: '#1976d2',
                backgroundColor: 'rgba(48, 146, 227, 0.04)',
              },
            }}
          >
            {t('view_fee_structure') || 'View Fee Structure'}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenPayFeesModal}
            sx={{
              bgcolor: '#3092e3',
              '&:hover': { bgcolor: '#1976d2' },
            }}
          >
            {t('pay_fees') || '+ Pay Fees'}
          </Button>
        </Box>
      </Box>

      {/* Enhanced Table for Fee Payment History */}
      <EnhancedTable
        columns={columns}
        data={feePaymentHistory}
        title={t('payment_history')}
        searchFields={['orderId', 'status']}
        cardProps={{
          elevation: 2,
        }}
      />

      {/* Pay Fees Modal */}
      <Dialog
        open={payFeesModalOpen}
        onClose={handleClosePayFeesModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PaymentIcon sx={{ color: '#3092e3' }} />
            {t('pay_fees_title')}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Student Name */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('student_name')}
                value={studentData.studentName || ''}
                disabled
                variant="outlined"
              />
            </Grid>

            {/* Class */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('class')}
                value={`${studentData.className || ''} ${studentData.section || ''}`.trim()}
                disabled
                variant="outlined"
              />
            </Grid>

            {/* Session */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('session')}
                value={studentData.sessionName || ''}
                disabled
                variant="outlined"
              />
            </Grid>

            {/* Payment Type */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>{t('payment_type')}</InputLabel>
                <Select
                  value={paymentType}
                  label={t('payment_type')}
                  onChange={handlePaymentTypeChange}
                  disabled={feeStructureLoading}
                >
                  <MenuItem value="INSTALLMENT">{t('installment')}</MenuItem>
                  <MenuItem value="ONE_TIME">{t('one_time_payment')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Amount */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('amount_rupee')}
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                disabled={feeStructureLoading || !feeStructure}
                InputProps={{
                  inputProps: { min: 0, step: 0.01 },
                }}
                onWheel={(e) => e.currentTarget.blur()}
                helperText={
                  feeStructureLoading
                    ? t('loading_fee_structure')
                    : !feeStructure
                      ? t('fee_structure_not_available')
                      : paymentType === 'ONE_TIME'
                        ? `${t('total_annual_fees_rupee')}${feeStructure.totalAnnualFees || 0}`
                        : `${t('first_installment_amount_rupee')}${feeStructure.feeInstallments?.[0]?.amount || 0}`
                }
              />
            </Grid>

            {feeStructureLoading && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePayFeesModal} color="inherit">
            {t('cancel')}
          </Button>
          <Button
            onClick={handlePayFees}
            variant="contained"
            disabled={!amount || createOrderMutation.isLoading || feeStructureLoading || !feeStructure}
            startIcon={
              createOrderMutation.isLoading ? (
                <CircularProgress size={20} sx={{ color: 'white' }} />
              ) : (
                <PaymentIcon />
              )
            }
            sx={{
              bgcolor: '#3092e3',
              '&:hover': { bgcolor: '#1976d2' },
            }}
          >
            {createOrderMutation.isLoading ? t('processing') : t('pay')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Success/Error Dialog */}
      <Dialog open={paymentDialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {paymentSuccess ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleIcon sx={{ color: 'success.main', mr: 1 }} />
              {t('payment_successful')}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CancelIcon sx={{ color: 'error.main', mr: 1 }} />
              {t('payment_failed')}
            </Box>
          )}
        </DialogTitle>
        <DialogContent>
          {paymentSuccess ? (
            <Typography>
              {t('payment_successfully_processed')}
            </Typography>
          ) : (
            <Typography color="error">
              {paymentError || t('payment_could_not_be_processed')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} variant="contained">
            {t('close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Fee Structure Dialog */}
      <Dialog
        open={viewFeeStructureModalOpen}
        onClose={handleCloseViewFeeStructureModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#f8f9fb',
            borderBottom: '2px solid #e0e7ff',
            pb: 2,
          }}
        >
          <Typography variant="h5" fontWeight="bold" sx={{ color: '#1a237e' }}>
            {t('fee_structure_details') || 'Fee Structure Details'}
          </Typography>
          <IconButton onClick={handleCloseViewFeeStructureModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, backgroundColor: '#ffffff' }}>
          {viewFeeStructureLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : !viewFeeStructure ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Alert severity="info">
                {t('fee_structure_not_available') || 'Fee structure not available for your class and session.'}
              </Alert>
            </Box>
          ) : (
            <Box sx={{ p: 4 }}>
              {/* School Logo, Name and Details */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
                {schoolLogoUrl && (
                  <Box
                    component="img"
                    src={schoolLogoUrl}
                    alt="School Logo"
                    sx={{
                      height: 80,
                      width: 'auto',
                      objectFit: 'contain',
                    }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography
                    variant="h5"
                    fontWeight="bold"
                    sx={{
                      color: '#1a237e',
                      fontSize: '40px',
                      mb: 0.5,
                    }}
                  >
                    {schoolData?.nameofSchool?.toUpperCase() || 'SCHOOL NAME'}
                  </Typography>
                  {(schoolData?.schoolCode || schoolData?.diseCode) && (
                    <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                      {schoolData?.schoolCode && (
                        <Typography variant="body2" sx={{ color: '#666', fontSize: '18px' }}>
                          <strong>{t('school_code_label')}</strong> {schoolData.schoolCode}
                        </Typography>
                      )}
                      {schoolData?.diseCode && (
                        <Typography variant="body2" sx={{ color: '#666', fontSize: '18px' }}>
                          <strong>{t('dise_code_label')}</strong> {schoolData.diseCode}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>

              {/* FEE STRUCTURE Title - Centered */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <Typography
                  variant="h2"
                  fontWeight="bold"
                  sx={{
                    color: '#1a237e',
                    fontSize: '30px',
                    textAlign: 'center',
                  }}
                >
                  {t('fee_structure_title')}
                </Typography>
              </Box>

              {/* Info Bar with CLASS, SESSION, FEE TYPE, TOTAL ANNUAL FEES */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 4,
                  p: 2,
                  backgroundColor: '#f5f5f5',
                  borderRadius: 2,
                  width: '100%',
                  flexWrap: 'wrap',
                  gap: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1" fontWeight="bold" sx={{ color: '#666' }}>
                    {t('class') || 'Class'}:
                  </Typography>
                  <Typography variant="body1" fontWeight="normal" sx={{ color: '#333' }}>
                    {studentData?.className || viewFeeStructure.className || '-'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1" fontWeight="bold" sx={{ color: '#666' }}>
                    {t('session') || 'Session'}:
                  </Typography>
                  <Typography variant="body1" fontWeight="normal" sx={{ color: '#333' }}>
                    {studentData?.sessionName || viewFeeStructure.sessionName || '-'}
                  </Typography>
                </Box>
                {/* Fee Type is now per-item */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1" fontWeight="bold" sx={{ color: '#666' }}>
                    {t('total_annual_fees') || 'Total Annual Fees'}:
                  </Typography>
                  <Typography variant="body1" fontWeight="normal" sx={{ color: '#1a237e' }}>
                    ₹{viewFeeStructure.totalAnnualFees?.toLocaleString('en-IN') || '0'}
                  </Typography>
                </Box>
              </Box>

              {/* Installments Table */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{
                    color: '#1a237e',
                    mb: 2,
                  }}
                >
                  {t('fee_installments_title')}
                </Typography>
                <TableContainer
                  component={Paper}
                  sx={{
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    borderRadius: 2,
                  }}
                >
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#1976d2' }}>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.95rem' }}>
                          {t('installment_number_label')}
                        </TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.95rem' }}>
                          {t('fee_type_label')}
                        </TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.95rem' }}>
                          {t('amount_rupee_label')}
                        </TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.95rem' }}>
                          {t('due_date_label')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                       {viewFeeStructure.feeInstallments && viewFeeStructure.feeInstallments.length > 0 ? (
                         [...viewFeeStructure.feeInstallments]
                           .sort((a, b) => {
                             const parseDueDate = (val: any): number => {
                               if (!val) return 0;
                               if (Array.isArray(val)) {
                                 const [year, month, day] = val;
                                 return new Date(year, month - 1, day).getTime();
                               }
                               return new Date(val).getTime();
                             };
                             return parseDueDate(a.dueDate) - parseDueDate(b.dueDate);
                           })
                           .map((installment, index) => {
                            let formattedDate = '-';

                            if (installment.dueDate) {
                              try {
                                let date: Date | null = null;
                                const dueDateValue = installment.dueDate as any;

                                if (Array.isArray(dueDateValue)) {
                                  const [year, month, day, hour = 0, minute = 0, second = 0] = dueDateValue;
                                  date = new Date(year, month - 1, day, hour, minute, second);
                                } else if (dueDateValue instanceof Date) {
                                  date = dueDateValue;
                                } else if (typeof dueDateValue === 'string') {
                                  date = new Date(dueDateValue);
                                  if (isNaN(date.getTime())) {
                                    const timestamp = parseInt(dueDateValue);
                                    if (!isNaN(timestamp)) {
                                      date = new Date(timestamp);
                                    }
                                  }
                                } else if (typeof dueDateValue === 'number') {
                                  date = new Date(dueDateValue);
                                }

                                if (date && !isNaN(date.getTime())) {
                                  formattedDate = format(date, 'dd/MM/yyyy');
                                }
                              } catch (error) {
                              }
                            }

                             const feeTypeDisplay = installment.feeTypeName === "TUITION_FEES"
                               ? t("tution_fee")
                               : installment.feeTypeName === "ADMISSION_FEES"
                               ? t("admission_fees")
                               : installment.feeTypeName === "TRANSPORT_FEES"
                               ? t("transport_fees")
                               : installment.feeTypeName || "-";

                            return (
                              <TableRow
                                key={index}
                                sx={{
                                  '&:nth-of-type(even)': {
                                    backgroundColor: '#f5f5f5',
                                  },
                                  '&:hover': {
                                    backgroundColor: '#e3f2fd',
                                  },
                                }}
                              >
                                <TableCell sx={{ fontWeight: 'medium', fontSize: '0.95rem' }}>
                                  {index + 1}
                                </TableCell>
                                <TableCell sx={{ fontSize: '0.95rem' }}>{feeTypeDisplay}</TableCell>
                                <TableCell sx={{ fontWeight: 'medium', fontSize: '0.95rem' }}>
                                  ₹{installment.amount?.toLocaleString('en-IN') || '0'}
                                </TableCell>
                                <TableCell sx={{ fontSize: '0.95rem' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <CalendarTodayIcon sx={{ fontSize: 16, color: '#666' }} />
                                    {formattedDate}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            );
                          })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                            <Typography variant="body2" color="text.secondary">
                              {t('no_installments') || 'No installments'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, backgroundColor: '#f8f9fb', borderTop: '1px solid #e0e0e0' }}>
          <Button
            onClick={handleCloseViewFeeStructureModal}
            sx={{
              backgroundColor: '#3092e3',
              '&:hover': { backgroundColor: '#1976d2' },
              color: 'white',
              px: 3,
            }}
          >
            {t('close') || 'Close'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FeePaymentManagement;
